import { zodResolver } from "@hookform/resolvers/zod";
import type { ColumnDef, FilterFn } from "@tanstack/react-table";
import { CheckCircle2, Plus, XCircle } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useParams } from "react-router";
import { toast } from "sonner";
import { z } from "zod";
import {
  DataTable,
  DataTableColumnHeader,
  DataTableDateTime,
  DataTablePagination,
  DataTableToolbar,
  useClientDataTable,
} from "@/components/data-table";
import { AccountMemberPicker } from "@/components/account/AccountMemberPicker";
import { ProductAccessSheet } from "@/components/account/ProductAccessSheet";
import {
  AdminAlert,
  AdminEmptyState,
  AdminPageHeader,
  AdminPill,
} from "@/components/shell/AdminPage";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ApiError, api } from "@/lib/api";
import { useAccountWorkspace } from "@/lib/account-workspace";
import { useAuth } from "@/lib/auth";
import { copyInvitationShareText } from "@/lib/invitation-share";
import { userAvatarInitial, userDisplayLabel } from "@/lib/user-display";
import { cn } from "@/lib/utils";

type MembershipUser = {
  display_name: string | null;
  display_label?: string | null;
  email: string | null;
  avatar_url: string | null;
  avatar_initial: string;
  online: boolean;
  last_activity_at: string | null;
  phone: string | null;
  wechat_bound: boolean;
  last_login_at: string | null;
  last_login_ip: string | null;
  last_login_location: string | null;
  last_login_user_agent: string | null;
  login_count: number;
};

type Membership = {
  id: number;
  user_id: number;
  role: "owner" | "admin" | "member";
  kind: "internal" | "external";
  source_authorization_id: number | null;
  disabled: boolean;
  disabled_at: string | null;
  joined_at: string;
  user: MembershipUser;
};

type Invitation = {
  id: number;
  account_id: number;
  contact: string;
  role: string;
  token: string;
  status: string;
  created_at?: string;
};

type InboundAuthorization = { grantor_account_id: number; permission: "read_only" | "maintain" };

type MemberRow =
  | { rowType: "invitation"; id: string; invitation: Invitation }
  | { rowType: "membership"; id: string; membership: Membership };

const roleSchema = z.enum(["owner", "admin", "member"]);
const optionalUserId = z
  .string()
  .trim()
  .transform((value) => (value === "" ? undefined : value))
  .pipe(z.string().regex(/^[1-9]\d*$/, "请输入有效的用户 ID").optional());
const invitationSchema = z.object({
  contact: z
    .string()
    .trim()
    .max(255, "联系方式不能超过 255 个字符")
    .transform((value) => (value === "" ? undefined : value))
    .pipe(z.string().max(255).optional()),
  user_id: optionalUserId,
  role: roleSchema,
});

const roleLabels: Record<string, string> = {
  owner: "所有者",
  admin: "管理员",
  member: "成员",
};

const editableRoles = ["admin", "member"] as const;
const MEMBERS_TABLE_STORAGE_KEY = "c456.datatable.members.column-visibility.v2";
const MEMBERS_FORCED_VISIBLE_COLUMNS = ["member", "role", "actions"];

type InvitationFormValues = z.infer<typeof invitationSchema>;

function summarizeBrowser(userAgent?: string | null) {
  if (!userAgent) return "—";
  if (/Edg\//.test(userAgent)) return "Edge";
  if (/Chrome\//.test(userAgent) && !/Edg\//.test(userAgent)) return "Chrome";
  if (/Firefox\//.test(userAgent)) return "Firefox";
  if (/Safari\//.test(userAgent) && !/Chrome\//.test(userAgent)) return "Safari";
  return "其他";
}

function rowPhone(row: MemberRow) {
  return row.rowType === "invitation" ? row.invitation.contact : row.membership.user.phone ?? "";
}

function rowLabel(row: MemberRow) {
  if (row.rowType === "invitation") return row.invitation.contact;
  const u = row.membership.user;
  return (
    u.display_label?.trim() ||
    userDisplayLabel({
      id: row.membership.user_id,
      display_name: u.display_name,
      email: u.email,
      phone: u.phone,
    })
  );
}

function rowEmail(row: MemberRow) {
  return row.rowType === "membership" ? row.membership.user.email?.trim() || "" : "";
}

function rowRole(row: MemberRow) {
  return row.rowType === "invitation" ? row.invitation.role : row.membership.role;
}

function rowJoinedAt(row: MemberRow) {
  return row.rowType === "invitation" ? row.invitation.created_at ?? "" : row.membership.joined_at;
}

function rowStatus(row: MemberRow) {
  if (row.rowType === "invitation") return "pending";
  return row.membership.disabled ? "disabled" : "active";
}

const membersGlobalFilter: FilterFn<MemberRow> = (row, _columnId, filterValue) => {
  const query = String(filterValue ?? "").trim().toLowerCase();
  if (!query) return true;
  const item = row.original;
  const haystack = [
    rowLabel(item),
    rowEmail(item),
    rowPhone(item),
    roleLabels[rowRole(item)] ?? rowRole(item),
    rowStatus(item),
    item.rowType === "membership" && item.membership.kind === "external" ? "外部" : "",
    item.rowType === "invitation" ? "邀请 待接受" : "",
  ]
    .join(" ")
    .toLowerCase();
  return haystack.includes(query);
};

export default function MembersPage() {
  const { accountId: id } = useParams();
  const { account } = useAccountWorkspace();
  const { user } = useAuth();
  const [memberships, setMemberships] = useState<Membership[]>([]);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [canManage, setCanManage] = useState(false);
  const [canMaintain, setCanMaintain] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  /** 转移所有权：选中的接收人 user_id（提交时映射为 membership_id） */
  const [transferTargetUserId, setTransferTargetUserId] = useState<number | null>(null);
  const [updatingId, setUpdatingId] = useState<number | null>(null);
  const [revokingId, setRevokingId] = useState<number | null>(null);
  const [transferring, setTransferring] = useState(false);
  const [confirmAction, setConfirmAction] = useState<
    | { type: "revoke"; invitation: Invitation }
    | { type: "disable"; membership: Membership }
    | { type: "enable"; membership: Membership }
    | null
  >(null);
  const [confirming, setConfirming] = useState(false);
  const [accessMembership, setAccessMembership] = useState<Membership | null>(null);

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
    setError: setFormError,
    clearErrors,
  } = useForm<InvitationFormValues>({
    resolver: zodResolver(invitationSchema),
    defaultValues: { contact: "", user_id: "", role: "member" },
  });

  const isMaintainer = !canManage && canMaintain;
  const canInvite = canManage || isMaintainer;
  const accountName = account?.name ?? `账户 #${id}`;
  const myMembership = memberships.find((item) => item.user_id === user?.id) ?? null;
  const isAccountOwner = myMembership?.role === "owner" && !myMembership.disabled;
  const transferCandidates = useMemo(
    () => memberships.filter((item) => item.kind === "internal" && item.role !== "owner" && !item.disabled),
    [memberships],
  );

  const tableRows = useMemo<MemberRow[]>(
    () => [
      ...invitations.map((invitation) => ({
        rowType: "invitation" as const,
        id: `invite-${invitation.id}`,
        invitation,
      })),
      ...memberships.map((membership) => ({
        rowType: "membership" as const,
        id: `member-${membership.id}`,
        membership,
      })),
    ],
    [invitations, memberships],
  );

  const loadInvitations = useCallback(async () => {
    if (!id || !canManage) {
      setInvitations([]);
      return;
    }
    try {
      const { invitations: pending } = await api.fetchForAccount<{ invitations: Invitation[] }>(
        id,
        `/accounts/${id}/invitations`,
      );
      setInvitations(pending);
    } catch {
      setInvitations([]);
    }
  }, [id, canManage]);

  useEffect(() => {
    if (isMaintainer) reset((values) => ({ ...values, role: "admin" }));
  }, [isMaintainer, reset]);

  useEffect(() => {
    if (!id) return;
    api
      .fetchForAccount<{ memberships: Membership[] }>(id, `/accounts/${id}/memberships`)
      .then(({ memberships: fetchedMemberships }) => {
        setMemberships(fetchedMemberships);
        const membership = fetchedMemberships.find((item) => item.user_id === user?.id);
        setCanManage(
          Boolean(membership && !membership.disabled && (membership.role === "owner" || membership.role === "admin")),
        );
      })
      .catch((caughtError) =>
        setError(caughtError instanceof ApiError ? caughtError.message : "无法加载成员"),
      );
  }, [id, user?.id]);

  useEffect(() => {
    void loadInvitations();
  }, [loadInvitations]);

  useEffect(() => {
    if (!id) return;
    api
      .fetch<{ third_party_authorizations: InboundAuthorization[] }>("/third_party_authorizations/inbound")
      .then(({ third_party_authorizations }) =>
        setCanMaintain(
          third_party_authorizations.some(
            (authorization) =>
              authorization.grantor_account_id === Number(id) && authorization.permission === "maintain",
          ),
        ),
      )
      .catch(() => setCanMaintain(false));
  }, [id]);

  async function copyInvite(invitation: Invitation) {
    try {
      await copyInvitationShareText({
        accountName,
        contact: invitation.contact,
        role: invitation.role,
        token: invitation.token,
      });
      toast.success("邀请文案已复制，可粘贴到微信发送");
    } catch {
      toast.error("复制失败，请检查浏览器剪贴板权限");
    }
  }

  async function updateRole(membership: Membership, role: (typeof editableRoles)[number]) {
    if (!id || membership.role === role || membership.role === "owner") return;
    setUpdatingId(membership.id);
    setError(null);
    try {
      const { membership: updatedMembership } = await api.fetchForAccount<{ membership: Membership }>(
        id,
        `/accounts/${id}/memberships/${membership.id}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ role }),
        },
      );
      setMemberships((current) =>
        current.map((item) => (item.id === updatedMembership.id ? updatedMembership : item)),
      );
      toast.success("角色已更新");
    } catch (caughtError) {
      setError(caughtError instanceof ApiError ? caughtError.message : "更新成员角色失败，请稍后重试");
    } finally {
      setUpdatingId(null);
    }
  }

  async function setDisabled(membership: Membership, disabled: boolean) {
    if (!id) return;
    setUpdatingId(membership.id);
    setError(null);
    try {
      const { membership: updatedMembership } = await api.fetchForAccount<{ membership: Membership }>(
        id,
        `/accounts/${id}/memberships/${membership.id}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ disabled }),
        },
      );
      setMemberships((current) =>
        current.map((item) => (item.id === updatedMembership.id ? updatedMembership : item)),
      );
      toast.success(disabled ? "已禁用该成员" : "已恢复该成员");
    } catch (caughtError) {
      setError(caughtError instanceof ApiError ? caughtError.message : "更新成员状态失败，请稍后重试");
    } finally {
      setUpdatingId(null);
    }
  }

  async function revokeInvitation(invitation: Invitation) {
    if (!id) return;
    setRevokingId(invitation.id);
    setError(null);
    try {
      await api.fetchForAccount(id, `/accounts/${id}/invitations/${invitation.id}`, { method: "DELETE" });
      setInvitations((current) => current.filter((item) => item.id !== invitation.id));
      toast.success("邀请已撤销");
    } catch (caughtError) {
      setError(caughtError instanceof ApiError ? caughtError.message : "撤销邀请失败，请稍后重试");
    } finally {
      setRevokingId(null);
    }
  }

  async function runConfirmAction() {
    if (!confirmAction) return;
    setConfirming(true);
    try {
      if (confirmAction.type === "revoke") {
        await revokeInvitation(confirmAction.invitation);
      } else if (confirmAction.type === "disable") {
        await setDisabled(confirmAction.membership, true);
      } else {
        await setDisabled(confirmAction.membership, false);
      }
      setConfirmAction(null);
    } finally {
      setConfirming(false);
    }
  }

  async function transferOwnership() {
    if (!id || transferTargetUserId == null) {
      setError("请选择要接收所有权的成员");
      return;
    }
    const membershipId = transferCandidates.find((item) => item.user_id === transferTargetUserId)?.id;
    if (membershipId == null) {
      setError("请选择要接收所有权的成员");
      return;
    }
    setTransferring(true);
    setError(null);
    try {
      const { memberships: updated } = await api.fetchForAccount<{ memberships: Membership[] }>(
        id,
        `/accounts/${id}/memberships/transfer_ownership`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ membership_id: membershipId }),
        },
      );
      setMemberships(updated);
      setCanManage(
        updated.some(
          (item) =>
            item.user_id === user?.id &&
            !item.disabled &&
            (item.role === "owner" || item.role === "admin"),
        ),
      );
      setTransferOpen(false);
      setTransferTargetUserId(null);
      toast.success("所有权已转移；你现在是管理员");
    } catch (caughtError) {
      setError(caughtError instanceof ApiError ? caughtError.message : "转移所有权失败，请稍后重试");
    } finally {
      setTransferring(false);
    }
  }

  async function onInvite({ contact, user_id, role }: InvitationFormValues) {
    if (!id) return;
    clearErrors();
    try {
      if (isMaintainer) {
        if (!user_id) {
          setFormError("user_id", { message: "请输入外部管理员的用户 ID" });
          return;
        }
        const { membership } = await api.fetchForAccount<{ membership: Membership }>(
          id,
          `/accounts/${id}/memberships`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ user_id: Number(user_id), role: "admin" }),
          },
        );
        setMemberships((current) => [...current, membership]);
        reset({ contact: "", role: "admin" });
        setInviteOpen(false);
        toast.success(`已添加外部成员 #${membership.user_id}`);
        return;
      }

      if (!contact) {
        setFormError("contact", { message: "请输入联系方式" });
        return;
      }
      const { invitation } = await api.fetchForAccount<{ invitation: Invitation }>(
        id,
        `/accounts/${id}/memberships`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ contact, role }),
        },
      );
      setInvitations((current) => [invitation, ...current.filter((item) => item.id !== invitation.id)]);
      reset({ contact: "", role: "member" });
      setInviteOpen(false);
      try {
        await copyInvitationShareText({
          accountName,
          contact: invitation.contact,
          role: invitation.role,
          token: invitation.token,
        });
        toast.success("邀请已创建，文案已复制，可粘贴发送给对方");
      } catch {
        toast.success("邀请已创建，请在列表中复制邀请信息");
      }
    } catch (caughtError) {
      setFormError("root", {
        message: caughtError instanceof ApiError ? caughtError.message : "邀请成员失败，请稍后重试",
      });
    }
  }

  const columns = useMemo<ColumnDef<MemberRow>[]>(
    () => [
      {
        id: "member",
        accessorFn: (row) => rowLabel(row),
        enableHiding: false,
        header: ({ column }) => <DataTableColumnHeader column={column} title="成员" />,
        meta: { label: "成员", headerClassName: "min-w-44" },
        cell: ({ row }) => {
          const item = row.original;
          if (item.rowType === "invitation") {
            return (
              <div>
                <div className="font-medium">{item.invitation.contact || "—"}</div>
                <div className="mt-1 text-xs text-muted-foreground">待加入邀请</div>
              </div>
            );
          }
          const membership = item.membership;
          const u = membership.user;
          const memberLabel = rowLabel(item);
          const initial = userAvatarInitial({
            email: u.email,
            display_name: u.display_name,
            avatar_initial: u.avatar_initial,
          });
          return (
            <div className="flex items-center gap-3">
              <Avatar className="size-9 shrink-0">
                {u.avatar_url ? <AvatarImage src={u.avatar_url} alt={memberLabel} /> : null}
                <AvatarFallback className="bg-primary/10 text-sm font-medium text-primary">
                  {initial}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="truncate font-medium">{memberLabel}</span>
                  {u.online ? (
                    <span className="inline-flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400">
                      <span className="size-2 shrink-0 rounded-full bg-emerald-500" aria-hidden />
                      在线
                    </span>
                  ) : null}
                </div>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {membership.user_id === user?.id ? <AdminPill tone="primary">我</AdminPill> : null}
                  {membership.kind === "external" ? <AdminPill tone="warning">外部成员</AdminPill> : null}
                </div>
              </div>
            </div>
          );
        },
      },
      {
        id: "email",
        accessorFn: (row) => rowEmail(row),
        header: ({ column }) => <DataTableColumnHeader column={column} title="邮箱" />,
        meta: { label: "邮箱", headerClassName: "min-w-40" },
        cell: ({ row }) => {
          const email = rowEmail(row.original);
          return email ? (
            <span className="font-mono text-sm">{email}</span>
          ) : (
            <span className="text-muted-foreground">—</span>
          );
        },
      },
      {
        id: "phone",
        accessorFn: (row) => rowPhone(row),
        header: ({ column }) => <DataTableColumnHeader column={column} title="手机号" />,
        meta: { label: "手机号", headerClassName: "min-w-36" },
        cell: ({ row }) => {
          const item = row.original;
          const phone = rowPhone(item) || "—";
          return <span className="font-mono text-sm font-medium">{phone}</span>;
        },
      },
      {
        id: "wechat",
        accessorFn: (row) =>
          row.rowType === "membership" && row.membership.user.wechat_bound ? 1 : 0,
        header: ({ column }) => <DataTableColumnHeader column={column} title="微信" />,
        meta: { label: "微信" },
        cell: ({ row }) => {
          const bound = row.original.rowType === "membership" && row.original.membership.user.wechat_bound;
          return bound ? (
            <span className="inline-flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400">
              <CheckCircle2 className="size-3.5" />
              已绑定
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
              <XCircle className="size-3.5 opacity-50" />
              未绑定
            </span>
          );
        },
      },
      {
        id: "role",
        accessorFn: (row) => rowRole(row),
        enableHiding: false,
        header: ({ column }) => <DataTableColumnHeader column={column} title="角色" />,
        meta: { label: "角色" },
        cell: ({ row }) => {
          const item = row.original;
          if (item.rowType === "invitation") {
            return <AdminPill>{roleLabels[item.invitation.role] ?? item.invitation.role}</AdminPill>;
          }
          const membership = item.membership;
          const isOwnerRow = membership.role === "owner";
          const canEditRole =
            !isOwnerRow &&
            !membership.disabled &&
            (canManage || (isMaintainer && membership.kind === "external"));
          if (isOwnerRow) return <AdminPill tone="success">所有者</AdminPill>;
          if (canEditRole) {
            return (
              <Select
                value={membership.role}
                disabled={updatingId === membership.id}
                onValueChange={(value) => updateRole(membership, value as (typeof editableRoles)[number])}
              >
                <SelectTrigger className="h-8 w-28" aria-label={`用户 #${membership.user_id} 的角色`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {editableRoles.map((value) => (
                    <SelectItem key={value} value={value}>
                      {roleLabels[value]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            );
          }
          return <AdminPill>{roleLabels[membership.role]}</AdminPill>;
        },
      },
      {
        id: "joined_at",
        accessorFn: (row) => rowJoinedAt(row),
        header: ({ column }) => <DataTableColumnHeader column={column} title="加入时间" />,
        meta: { label: "加入时间", headerClassName: "min-w-28" },
        cell: ({ row }) => <DataTableDateTime value={rowJoinedAt(row.original)} />,
      },
      {
        id: "last_login_at",
        accessorFn: (row) =>
          row.rowType === "membership" ? row.membership.user.last_login_at ?? "" : "",
        header: ({ column }) => <DataTableColumnHeader column={column} title="最后登录" />,
        meta: { label: "最后登录", headerClassName: "min-w-28" },
        cell: ({ row }) => (
          <DataTableDateTime
            value={
              row.original.rowType === "membership" ? row.original.membership.user.last_login_at : null
            }
          />
        ),
      },
      {
        id: "location",
        accessorFn: (row) => {
          if (row.rowType !== "membership") return "";
          return [row.membership.user.last_login_ip, row.membership.user.last_login_location]
            .filter(Boolean)
            .join(" ");
        },
        header: ({ column }) => <DataTableColumnHeader column={column} title="IP / 地区" />,
        meta: {
          label: "IP / 地区",
          headerClassName: "min-w-40",
          cellClassName: "max-w-44 truncate text-muted-foreground",
        },
        cell: ({ row }) => {
          if (row.original.rowType !== "membership") return "—";
          const label = [row.original.membership.user.last_login_ip, row.original.membership.user.last_login_location]
            .filter(Boolean)
            .join(" · ");
          return <span title={label || undefined}>{label || "—"}</span>;
        },
      },
      {
        id: "browser",
        accessorFn: (row) =>
          row.rowType === "membership"
            ? summarizeBrowser(row.membership.user.last_login_user_agent)
            : "",
        header: ({ column }) => <DataTableColumnHeader column={column} title="浏览器" />,
        meta: { label: "浏览器", cellClassName: "text-muted-foreground" },
        cell: ({ row }) =>
          row.original.rowType === "membership"
            ? summarizeBrowser(row.original.membership.user.last_login_user_agent)
            : "—",
      },
      {
        id: "login_count",
        accessorFn: (row) =>
          row.rowType === "membership" ? row.membership.user.login_count ?? 0 : -1,
        header: ({ column }) => (
          <DataTableColumnHeader column={column} title="登录次数" className="justify-end" />
        ),
        meta: {
          label: "登录次数",
          headerClassName: "text-right",
          cellClassName: "text-right tabular-nums",
        },
        cell: ({ row }) =>
          row.original.rowType === "membership" ? row.original.membership.user.login_count ?? 0 : "—",
      },
      {
        id: "status",
        accessorFn: (row) => rowStatus(row),
        header: ({ column }) => <DataTableColumnHeader column={column} title="状态" />,
        meta: { label: "状态" },
        cell: ({ row }) => {
          const status = rowStatus(row.original);
          if (status === "pending") return <AdminPill tone="warning">待接受</AdminPill>;
          if (status === "disabled") return <AdminPill tone="warning">已禁用</AdminPill>;
          return <AdminPill tone="success">正常</AdminPill>;
        },
      },
      {
        id: "actions",
        enableHiding: false,
        enableSorting: false,
        header: () => <div className="text-right">操作</div>,
        meta: {
          label: "操作",
          headerClassName: "min-w-44 text-right",
          cellClassName: "text-right",
          sticky: "right",
        },
        cell: ({ row }) => {
          const item = row.original;
          if (item.rowType === "invitation") {
            const invitation = item.invitation;
            return (
              <div className="flex flex-wrap items-center justify-end gap-1">
                <Button type="button" variant="outline" size="sm" onClick={() => void copyInvite(invitation)}>
                  复制邀请
                </Button>
                {canManage ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={revokingId === invitation.id}
                    onClick={() => setConfirmAction({ type: "revoke", invitation })}
                  >
                    撤销
                  </Button>
                ) : null}
              </div>
            );
          }

          const membership = item.membership;
          const isOwnerRow = membership.role === "owner";
          const canToggleDisabled = canManage && !isOwnerRow;
          const canEditProductAccess = canManage && !membership.disabled;

          return (
            <div className="flex flex-wrap items-center justify-end gap-1">
              {canEditProductAccess ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setAccessMembership(membership)}
                >
                  产品授权
                </Button>
              ) : null}
              {isOwnerRow && isAccountOwner ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setError(null);
                    setTransferTargetUserId(transferCandidates[0]?.user_id ?? null);
                    setTransferOpen(true);
                  }}
                >
                  转移所有权
                </Button>
              ) : null}
              {canToggleDisabled ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={updatingId === membership.id}
                  onClick={() =>
                    setConfirmAction({
                      type: membership.disabled ? "enable" : "disable",
                      membership,
                    })
                  }
                >
                  {membership.disabled ? "恢复" : "禁用"}
                </Button>
              ) : null}
              {!canEditProductAccess && !(isOwnerRow && isAccountOwner) && !canToggleDisabled ? (
                <span className="text-xs text-muted-foreground">—</span>
              ) : null}
            </div>
          );
        },
      },
    ],
    [
      canManage,
      isAccountOwner,
      isMaintainer,
      revokingId,
      transferCandidates,
      updatingId,
      user?.id,
    ],
  );

  const { table, globalFilter, setGlobalFilter } = useClientDataTable({
    data: tableRows,
    columns,
    storageKey: MEMBERS_TABLE_STORAGE_KEY,
    defaultPageSize: 10,
    globalFilterFn: membersGlobalFilter,
    forcedVisibleColumnIds: MEMBERS_FORCED_VISIBLE_COLUMNS,
    // 默认可见：成员（头像+昵称）、邮箱、手机号、微信、角色、加入时间、状态、操作
    // 其余列可通过「列显示」打开
    defaultColumnVisibility: {
      last_login_at: false,
      location: false,
      browser: false,
      login_count: false,
    },
  });

  const hasSourceRows = memberships.length > 0 || invitations.length > 0;

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="成员"
        description="查看成员资料、登录情况与邀请状态；可调整角色、禁用账号或转移所有权。"
        actions={
          canInvite ? (
            <Button
              type="button"
              onClick={() => {
                clearErrors();
                reset({ contact: "", user_id: "", role: isMaintainer ? "admin" : "member" });
                setInviteOpen(true);
              }}
            >
              <Plus className="size-4" />
              {isMaintainer ? "添加外部成员" : "邀请成员"}
            </Button>
          ) : null
        }
      />

      {error && <AdminAlert>{error}</AdminAlert>}

      {!hasSourceRows ? (
        <AdminEmptyState>暂无成员与邀请。点击右上角邀请成员。</AdminEmptyState>
      ) : (
        <div className="space-y-3">
          <DataTableToolbar
            table={table}
            searchValue={globalFilter}
            onSearchChange={setGlobalFilter}
            searchPlaceholder="搜索昵称、邮箱、手机号、角色…"
          />
          <DataTable
            table={table}
            emptyMessage="没有匹配的成员或邀请"
            getRowClassName={(row) =>
              cn(
                row.rowType === "invitation" && "bg-muted/20",
                row.rowType === "membership" && row.membership.disabled && "opacity-60",
              )
            }
          />
          <DataTablePagination table={table} />
        </div>
      )}

      <Dialog
        open={inviteOpen}
        onOpenChange={(open) => {
          setInviteOpen(open);
          if (!open) clearErrors();
        }}
      >
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{isMaintainer ? "添加外部成员" : "邀请成员"}</DialogTitle>
            <DialogDescription>
              {isMaintainer
                ? "以代管身份添加外部管理员；仅可管理外部成员。"
                : "创建后会出现在列表中，并自动复制邀请文案与链接，可发给对方。"}
            </DialogDescription>
          </DialogHeader>
          <form className="space-y-4" onSubmit={handleSubmit(onInvite)} noValidate>
            {isMaintainer ? (
              <div className="space-y-2">
                <Label htmlFor="invite-user-id">外部成员用户 ID</Label>
                <Input
                  id="invite-user-id"
                  type="number"
                  {...register("user_id")}
                  aria-invalid={Boolean(errors.user_id)}
                />
                {errors.user_id && (
                  <p className="text-sm text-destructive" role="alert">
                    {errors.user_id.message}
                  </p>
                )}
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  <Label htmlFor="invite-contact">联系方式</Label>
                  <Input id="invite-contact" {...register("contact")} aria-invalid={Boolean(errors.contact)} />
                  {errors.contact && (
                    <p className="text-sm text-destructive" role="alert">
                      {errors.contact.message}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>角色</Label>
                  <Controller
                    name="role"
                    control={control}
                    render={({ field }) => (
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger aria-invalid={Boolean(errors.role)}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {editableRoles.map((value) => (
                            <SelectItem key={value} value={value}>
                              {roleLabels[value]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                </div>
              </>
            )}
            {errors.root && <AdminAlert>{errors.root.message}</AdminAlert>}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setInviteOpen(false)}>
                取消
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "提交中…" : isMaintainer ? "添加" : "创建并复制邀请"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={transferOpen}
        onOpenChange={(open) => {
          setTransferOpen(open);
          if (!open) setTransferTargetUserId(null);
        }}
      >
        <DialogContent className="flex max-h-[min(85vh,640px)] flex-col gap-4 overflow-hidden sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>转移所有权</DialogTitle>
            <DialogDescription>
              转移后对方成为所有者，你将变为管理员。所有者角色不能直接改成成员或管理员。
            </DialogDescription>
          </DialogHeader>
          {transferCandidates.length === 0 ? (
            <AdminAlert tone="info">请先邀请其他内部成员，再转移所有权。</AdminAlert>
          ) : (
            <div className="min-h-0 space-y-2">
              <Label>接收人</Label>
              <AccountMemberPicker
                mode="single"
                members={transferCandidates}
                value={transferTargetUserId}
                onChange={setTransferTargetUserId}
                disabled={transferring}
                emptyText="暂无可用内部成员"
              />
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setTransferOpen(false)}>
              取消
            </Button>
            <Button
              type="button"
              disabled={transferring || transferCandidates.length === 0 || transferTargetUserId == null}
              onClick={() => void transferOwnership()}
            >
              {transferring ? "转移中…" : "确认转移"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(confirmAction)}
        onOpenChange={(open) => {
          if (!open && !confirming) setConfirmAction(null);
        }}
      >
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {confirmAction?.type === "revoke"
                ? "撤销邀请"
                : confirmAction?.type === "disable"
                  ? "禁用成员"
                  : "恢复成员"}
            </DialogTitle>
            <DialogDescription>
              {confirmAction?.type === "revoke"
                ? `确定撤销对 ${confirmAction.invitation.contact} 的邀请吗？撤销后对方将无法通过该链接加入。`
                : confirmAction?.type === "disable"
                  ? `确定禁用用户 #${confirmAction.membership.user_id}（${confirmAction.membership.user.phone || "无手机号"}）吗？禁用后其将无法进入本账户。`
                  : confirmAction
                    ? `确定恢复用户 #${confirmAction.membership.user_id}（${confirmAction.membership.user.phone || "无手机号"}）吗？恢复后可重新进入本账户。`
                    : null}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" disabled={confirming} onClick={() => setConfirmAction(null)}>
              取消
            </Button>
            <Button
              type="button"
              variant={confirmAction?.type === "enable" ? "default" : "destructive"}
              disabled={confirming}
              onClick={() => void runConfirmAction()}
            >
              {confirming ? "处理中…" : "确认"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {id && accessMembership ? (
        <ProductAccessSheet
          open={Boolean(accessMembership)}
          onOpenChange={(next) => {
            if (!next) setAccessMembership(null);
          }}
          accountId={id}
          membershipId={accessMembership.id}
          memberLabel={
            accessMembership.user.display_label?.trim() ||
            userDisplayLabel({
              id: accessMembership.user_id,
              display_name: accessMembership.user.display_name,
              email: accessMembership.user.email,
              phone: accessMembership.user.phone,
            })
          }
        />
      ) : null}
    </div>
  );
}
